import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Icon from "./Icon";
import { Reveal } from "./motion/Reveal";

export function DestinationCard({ d, large = false }) {
  return (
    <Link
      to={`/destinations/${d.slug}`}
      className={`group relative block overflow-hidden rounded-2xl bg-navy-dark shadow-sm transition-shadow duration-300 hover:shadow-[0_24px_60px_-18px_rgba(13,24,51,0.4)] ${
        large ? "min-h-[380px] sm:min-h-[440px]" : "min-h-[300px]"
      }`}
      data-testid={`destination-card-${d.slug}`}
    >
      <img
        src={d.image}
        alt={`Study in ${d.name}`}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/30 to-transparent" />
      <div className="relative flex h-full flex-col justify-end p-6 sm:p-7">
        <span className="mb-2 inline-flex w-max items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-white/80 backdrop-blur-sm">
          <Icon name="MapPin" className="h-3 w-3" /> {d.name}
        </span>
        <h3 className={`font-display font-semibold text-white ${large ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{d.tagline}</h3>
        <p className="mt-2 line-clamp-2 max-w-md text-sm leading-relaxed text-white/70">{d.description}</p>
        {d.popularPrograms && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {d.popularPrograms.slice(0, 3).map((p) => (
              <span key={p.title} className="rounded-full border border-white/20 px-2.5 py-0.5 text-[11px] text-white/70">{p.title}</span>
            ))}
          </div>
        )}
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-dot-amber">
          Explore Destination
          <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1.5" />
        </span>
      </div>
    </Link>
  );
}

export function InstitutionCard({ i }) {
  const initials = i.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  const location = i.city && i.city !== i.country ? `${i.city}, ${i.country}` : i.country;
  return (
    <div
      className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
      data-testid={`institution-card-${i.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-100 bg-white">
          <span className="flex h-full w-full items-center justify-center rounded-lg bg-navy font-display text-lg font-semibold text-white">{initials}</span>
          {i.logo && (
            <img
              src={i.logo}
              alt={`${i.name} logo`}
              loading="lazy"
              className="absolute inset-0 h-full w-full bg-white object-contain p-1.5"
              onError={(e) => e.currentTarget.remove()}
            />
          )}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-sand px-2.5 py-1 text-[11px] font-medium text-slate-600">
          <Icon name="MapPin" className="h-3 w-3" /> {location}
        </span>
      </div>
      <Link to={`/institutes/${i.slug}`} className="mt-4 block" data-testid={`institution-link-${i.slug}`}>
        <h3 className="font-display text-xl font-semibold leading-snug text-navy-dark transition-colors duration-200 hover:text-dot-blue">{i.name}</h3>
      </Link>
      <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">{i.description}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {i.programs.slice(0, 3).map((p) => (
          <span key={p} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] text-slate-500">{p}</span>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <Link
          to={`/institutes/${i.slug}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-200 hover:text-dot-blue"
        >
          View Details
          <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
        {i.website && (
          <a
            href={i.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            data-testid={`institute-website-${i.slug}`}
          >
            Visit Website
            <Icon name="ArrowUpRight" className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

export function ScholarshipCard({ s }) {
  return (
    <div
      className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
      data-testid={`scholarship-card-${s.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-dot-green/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-dot-green">{s.type}</span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
          <Icon name="MapPin" className="h-3 w-3" /> {s.country}
        </span>
      </div>
      <h3 className="mt-4 font-display text-xl font-semibold text-navy-dark">{s.name}</h3>
      <dl className="mt-4 flex-1 space-y-2.5 text-sm">
        <div><dt className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-slate-400">Study Level</dt><dd className="text-slate-700">{s.studyLevel}</dd></div>
        <div><dt className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-slate-400">Eligibility</dt><dd className="text-slate-700">{s.eligibility}</dd></div>
        <div><dt className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-slate-400">Coverage</dt><dd className="text-slate-700">{s.coverage}</dd></div>
        <div><dt className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-slate-400">Deadline</dt><dd className="inline-flex items-center gap-1.5 text-slate-700"><Icon name="Calendar" className="h-3.5 w-3.5 text-slate-400" />{s.deadline}</dd></div>
      </dl>
    </div>
  );
}

export function NewsCard({ a }) {
  return (
    <Link
      to={`/news/${a.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
      data-testid={`news-card-${a.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy backdrop-blur-sm">{a.category}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <time className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-slate-400">{a.publishedDate}</time>
        <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-navy-dark transition-colors duration-200 group-hover:text-navy">{a.title}</h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-navy">
          Read More
          <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function ServiceCard({ s, index = 0 }) {
  const dotColors = ["bg-dot-red", "bg-dot-green", "bg-dot-blue", "bg-dot-amber"];
  return (
    <Reveal delay={(index % 4) * 0.08} className="h-full">
      <div
        className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
        data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
          <Icon name={s.icon} className="h-5 w-5" />
        </span>
        <h3 className="mt-4 flex items-center gap-2 font-display text-lg font-semibold text-navy-dark">
          <span className={`h-1.5 w-1.5 rounded-full ${dotColors[index % 4]}`} />
          {s.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.text || s.short}</p>
      </div>
    </Reveal>
  );
}

export function TestimonialCard({ t, index }) {
  const initials = t.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <Reveal delay={index * 0.1} className="h-full">
      <figure
        className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/20 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
        data-testid={`testimonial-card-${index}`}
      >
        <div className="flex items-center justify-between">
          <Icon name="Quote" className="h-6 w-6 text-dot-amber" />
          <span className="flex gap-0.5" aria-label="5 out of 5 stars">
            {[0, 1, 2, 3, 4].map((i) => (
              <Icon key={i} name="Star" className="h-3.5 w-3.5 fill-dot-amber text-dot-amber" />
            ))}
          </span>
        </div>
        <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">
          &ldquo;{t.quote}&rdquo;
        </blockquote>
        <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy font-display text-sm font-semibold text-white">
            {initials}
          </span>
          <div>
            <p className="text-sm font-semibold text-navy-dark">{t.name}</p>
            <p className="text-xs text-slate-500">{t.program} — {t.destination}</p>
          </div>
        </figcaption>
      </figure>
    </Reveal>
  );
}

export function PlaceholderNote({ children }) {
  return (
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="mt-8 inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 bg-sand px-4 py-2 text-xs text-slate-500"
      data-testid="placeholder-note"
    >
      <Icon name="Eye" className="h-3.5 w-3.5" />
      {children}
    </motion.p>
  );
}
